import { toPointExpiresAtParam } from 'admin/utils/pointExpiry';
import React from 'react';
import { createCampaign } from '~/javascripts/admin/actions/campaign';
import EditCampaign, { CampaignForm } from '../../organisms/campaign/EditCampaign';
import CampaignModal from '../CampaignModal';

const CreateCampaign = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [apiMessages, setApiMessages] = React.useState<{ text: string; isError?: boolean }[]>([]);
  const [requestParams, setRequestParams] = React.useState<any>({});
  const [campaignForm, setCampaignForm] = React.useState<CampaignForm | null>(null);

  const onCreateCampaign = (campaign: CampaignForm) => {
    setApiMessages([]);

    const params = {
      name: campaign.name,
      description: campaign.description,
      private_money_id: campaign.privateMoney.id,
      starts_at: campaign.dateRange?.from,
      ends_at: campaign.dateRange?.to,
      bear_point_shop_id: campaign.pointBearerShop?.id,
      is_exclusive: campaign.isExclusive,
      event: campaign.event,
      priority: parseInt(campaign.priority?.toString() ?? '0', 10),
      subject:
        campaign.event === 'external-transaction' ||
        !['moneyAmountUniform', 'moneyAmountCustom'].includes(campaign.selectedPointRulesType)
          ? 'all'
          : 'money',
      ...(campaign.budgetCapsAmount && {
        budget_caps_amount: Number(campaign.budgetCapsAmount),
      }),
      ...(campaign.maxTotalPointAmount && {
        max_total_point_amount: Number(campaign.maxTotalPointAmount),
      }),
      ...(campaign.maxPointAmount && {
        max_point_amount: Number(campaign.maxPointAmount),
      }),
      ...(campaign.event === 'topup' &&
        campaign.applicableTransactionMetadata && {
          applicable_transaction_metadata: {
            key: 'topup-method-type',
            value: campaign.applicableTransactionMetadata,
          },
        }),
      ...(campaign.residentCheck !== 'none' && {
        applicable_account_metadata: {
          key: 'is_resident',
          value: 'true',
          ...(campaign.residentCheck === 'required' ? {} : { test: 'not-equal' }),
        },
      }),
      ...(campaign.shopSpecification === 'whitelist' &&
        campaign.applicableShops.length > 0 && {
          applicable_shop_ids: campaign.applicableShops.map((s) => s.id),
        }),
      ...(campaign.shopSpecification === 'blacklist' &&
        campaign.applicableShops.length > 0 && {
          blacklisted_shop_ids: campaign.applicableShops.map((s) => s.id),
        }),
      ...(campaign.shopSpecification === 'labels' && {
        applicable_shop_label_ids: campaign.applicableShopLabels.flatMap((r) => r.itemIds),
      }),
      ...(campaign.pointExpiresAt && {
        point_expires_at: toPointExpiresAtParam(campaign.pointExpiresAt),
      }),
      ...(Number.isFinite(parseInt(campaign.pointExpiresInDays?.toString() ?? '')) && {
        point_expires_in_days: Number(campaign.pointExpiresInDays),
      }),
      ...(campaign.daysOfWeek.length && {
        applicable_days_of_week: campaign.daysOfWeek,
      }),
      ...(campaign.timeRanges.length && {
        applicable_time_ranges: campaign.timeRanges,
      }),
      ...(['allAmountUniform', 'moneyAmountUniform'].includes(campaign.selectedPointRulesType) && {
        amount_based_point_rules: [
          {
            point_amount: Number(campaign.amountBasedPointRules.uniformAmountRule.amount),
            point_amount_unit: campaign.amountBasedPointRules.uniformAmountRule.unit,
          },
        ],
      }),
      ...(['allAmountCustom', 'moneyAmountCustom'].includes(campaign.selectedPointRulesType) && {
        amount_based_point_rules: campaign.amountBasedPointRules.customAmountRules.map((rule) => ({
          ...(rule.amount !== null && { point_amount: rule.amount }),
          ...(rule.unit && { point_amount_unit: rule.unit }),
          ...(rule.from !== null && { subject_more_than_or_equal: rule.from }),
          ...(rule.to !== null && { subject_less_than_or_equal: rule.to }),
        })),
      }),
      ...(campaign.selectedPointRulesType === 'product' && {
        product_based_point_rules: campaign.productBasedPointRules.productBasedPointRules,
      }),
      ...(campaign.selectedPointRulesType === 'product' &&
        campaign.productBasedPointRules.productBasedPointRules.length > 1 &&
        campaign.productBasedPointRules.miniumNumberForCombinationPurchase && {
          minimum_number_for_combination_purchase: Number(
            campaign.productBasedPointRules.miniumNumberForCombinationPurchase
          ),
        }),
    };

    setCampaignForm(campaign);
    setRequestParams(params);
    setIsModalOpen(true);
  };

  const onConfirm = () => {
    createCampaign(requestParams)
      .then(() => {
        setApiMessages([
          {
            text: 'Registered a campaign: キャンペーンを登録しました',
          },
        ]);
      })
      .catch((err) => {
        err.then((er) => {
          setApiMessages([
            {
              text: `Could not registered a campaign: ${er.message}.`,
              isError: true,
            },
          ]);
        });
      });

    setIsModalOpen(false);
  };

  return (
    <>
      <EditCampaign mode="create" onSubmit={onCreateCampaign} messages={apiMessages} />

      <CampaignModal
        isOpen={isModalOpen}
        campaign={campaignForm}
        onClose={() => setIsModalOpen(false)}
        onConfirm={onConfirm}
      />
    </>
  );
};

export default CreateCampaign;
